import { useEffect, useRef, useState } from "react";
import { X, Download, Share2, Linkedin, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const LEVEL_COLOR = {
  "Crítico": "#EF4444",
  "En desarrollo": "#F59E0B",
  "Funcional": "#3B82F6",
  "Sólido": "#10B981",
};

const AREA_SHORT = { A: "Marca", B: "Selección", C: "Onboarding", D: "Evaluación", E: "Feedback", F: "Desarrollo" };

/**
 * Modal que renderiza en un <canvas> 1080x1080 un "certificado" compartible
 * (estilo social) con la puntuación global, nivel y fecha. Permite descargarlo
 * como PNG o compartirlo con Web Share API si está disponible.
 */
export default function ShareableCertificate({ data, onClose }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  // Cuando el usuario pulsa "Compartir en LinkedIn", guardamos el texto
  // aquí para mostrarlo en una caja visible (por si la copia automática
  // al portapapeles falla o se pierde antes de llegar a LinkedIn).
  const [lastShareText, setLastShareText] = useState(null);
  const [copiedAgain, setCopiedAgain] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ctx = canvas.getContext("2d");
    const W = 1080;
    const H = 1080;

    // Fondo con gradiente diagonal
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#0f6e56");
    grad.addColorStop(0.55, "#22a888");
    grad.addColorStop(1, "#34d399");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Overlay radial suave (top-left)
    const rad = ctx.createRadialGradient(220, 220, 0, 220, 220, 700);
    rad.addColorStop(0, "rgba(255,255,255,0.14)");
    rad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = rad;
    ctx.fillRect(0, 0, W, H);

    // Marca
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.font = "700 26px Inter, Helvetica, Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("OptimizaRRHH", 90, 130);
    ctx.font = "500 18px Inter, Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.fillText("Talent Health Check", 90, 160);

    // Píldora "Informe personalizado"
    ctx.fillStyle = "rgba(255,255,255,0.14)";
    _roundRect(ctx, 90, 260, 340, 46, 23);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = 1;
    _roundRect(ctx, 90, 260, 340, 46, 23);
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 15px Inter, Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("DIAGNÓSTICO COMPLETADO", 90 + 170, 260 + 30);

    // Título grande
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 66px Inter, Helvetica, Arial, sans-serif";
    ctx.fillText("Mi diagnóstico", 90, 400);
    ctx.fillText("de gestión del talento", 90, 470);

    // Score gigante
    const score = data.overall_score ?? 0;
    ctx.font = "800 260px Inter, Helvetica, Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(String(score), 90, 750);
    // "/120"
    const scoreWidth = ctx.measureText(String(score)).width;
    ctx.font = "700 66px Inter, Helvetica, Arial, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillText("/120", 90 + scoreWidth + 14, 750);

    // Badge nivel
    const level = data.overall_level ?? "";
    const levelColor = LEVEL_COLOR[level] || "#10B981";
    ctx.font = "700 22px Inter, Helvetica, Arial, sans-serif";
    const badgeText = `NIVEL · ${level.toUpperCase()}`;
    const bw = ctx.measureText(badgeText).width + 60;
    ctx.fillStyle = "#ffffff";
    _roundRect(ctx, 90, 790, bw, 56, 28);
    ctx.fill();
    ctx.fillStyle = levelColor;
    ctx.textAlign = "left";
    ctx.fillText(badgeText, 90 + 30, 790 + 36);

    // Mini barras por área (fila inferior)
    const areas = (data.area_rows || []).slice(0, 6);
    const barsY = 900;
    const gap = 24;
    const barW = (W - 180 - gap * 5) / 6;
    areas.forEach((a, i) => {
      const x = 90 + i * (barW + gap);
      // fondo
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      _roundRect(ctx, x, barsY + 30, barW, 6, 3);
      ctx.fill();
      // relleno
      const fillW = barW * (a.score / 20);
      ctx.fillStyle = "#ffffff";
      _roundRect(ctx, x, barsY + 30, fillW, 6, 3);
      ctx.fill();
      // letra código
      ctx.fillStyle = "#ffffff";
      ctx.font = "800 22px Inter, Helvetica, Arial, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(a.code, x, barsY);
      // nombre corto
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.font = "600 14px Inter, Helvetica, Arial, sans-serif";
      ctx.fillText(AREA_SHORT[a.code] || "", x, barsY + 20);
      // puntuación
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 16px Inter, Helvetica, Arial, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(`${a.score}/20`, x + barW, barsY);
    });

    // Fecha + web
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.font = "500 20px Inter, Helvetica, Arial, sans-serif";
    ctx.textAlign = "left";
    const dateStr = _formatDateEs(data.created_at);
    ctx.fillText(dateStr, 90, 1015);
    ctx.textAlign = "right";
    ctx.fillText("talenthealthcheck.com", W - 90, 1015);

    setReady(true);
  }, [data]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  async function download() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    return await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return resolve(null);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `talent-health-check-${data.overall_score}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        resolve(blob);
      }, "image/png");
    });
  }

  // Texto que el usuario publicará junto a la imagen del certificado.
  // Se mantiene corto y "humano" — LinkedIn recorta descriptions largas.
  function buildShareText() {
    const scoreLine = `${data.overall_score}/120 · nivel ${data.overall_level}`;
    return (
      `Acabo de realizar el diagnóstico de gestión del talento de Talent Health Check y mis resultados son: ${scoreLine}.\n\n` +
      `Es un test rápido que evalúa 6 áreas clave de gestión del talento en cualquier empresa. Si te interesa mejorar la gestión de personas de tu organización, te recomiendo probarlo:\n\n` +
      `https://www.optimizarrhh.com\n\n` +
      `#GestiónDelTalento #RecursosHumanos #OptimizaRRHH`
    );
  }

  async function copyText(text) {
    // Ruta moderna
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_e) {
        // sigue con el fallback
      }
    }
    // Fallback: textarea temporal + execCommand
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (_e) {
      return false;
    }
  }

  async function share() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const text = buildShareText();
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "talent-health-check.png", { type: "image/png" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text, title: "Mi Talent Health Check" });
          return;
        }
      } catch (_e) {
        // usuario canceló → salimos silenciosamente
        return;
      }
      // Sin soporte → fallback a copiar+descargar
      await copyText(text);
      await download();
      toast.info("Texto copiado y certificado descargado. Ábrelo en tu red social preferida.");
    }, "image/png");
  }

  async function shareLinkedIn() {
    const text = buildShareText();
    // 1) Copiamos el texto al portapapeles para que el usuario solo tenga
    //    que pegar (Ctrl+V) en el compositor de LinkedIn.
    const copied = await copyText(text);
    // 2) Descargamos la imagen del certificado a su equipo.
    await download();
    // 3) Abrimos el compositor de LinkedIn en una pestaña nueva.
    //    Nota: LinkedIn ya NO permite pre-poblar texto vía URL, así que la
    //    UX real es: pegar el texto + adjuntar la imagen descargada.
    window.open(
      "https://www.linkedin.com/feed/?shareActive=true",
      "_blank",
      "noopener,noreferrer",
    );
    if (copied) {
      toast.success(
        "Texto copiado al portapapeles y certificado descargado. Pega el texto (Ctrl+V) en LinkedIn y arrastra la imagen que te acabamos de descargar.",
        { duration: 8000 },
      );
    } else {
      toast.info(
        "Certificado descargado. Copia el texto que aparece debajo del certificado y pégalo en LinkedIn junto con la imagen.",
        { duration: 8000 },
      );
    }
    setLastShareText(text);
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 fade-in-up"
      onClick={onClose}
      data-testid="share-certificate-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="font-display font-semibold text-gray-900 text-[15px]">
              Comparte tu diagnóstico
            </div>
            <div className="text-xs text-gray-500">
              Descarga la imagen o compártela directamente
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            data-testid="share-certificate-close-btn"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </header>
        <div className="p-5 md:p-6">
          <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-lg mx-auto" style={{ maxWidth: 520 }}>
            <canvas
              ref={canvasRef}
              width={1080}
              height={1080}
              className="w-full block"
              data-testid="share-certificate-canvas"
            />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-2.5 justify-center">
            <button
              onClick={async () => {
                const ok = await download();
                if (ok) toast.success("Certificado descargado");
              }}
              disabled={!ready}
              className="btn-primary disabled:opacity-50"
              data-testid="share-certificate-download-btn"
            >
              <Download className="w-4 h-4" strokeWidth={2} /> Descargar imagen
            </button>
            <button
              onClick={shareLinkedIn}
              disabled={!ready}
              className="btn-secondary disabled:opacity-50"
              data-testid="share-certificate-linkedin-btn"
            >
              <Linkedin className="w-4 h-4" strokeWidth={2} /> Compartir en LinkedIn
            </button>
            {typeof navigator !== "undefined" && navigator.canShare && (
              <button
                onClick={share}
                disabled={!ready}
                className="btn-secondary disabled:opacity-50"
                data-testid="share-certificate-native-btn"
              >
                <Share2 className="w-4 h-4" strokeWidth={2} /> Compartir…
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">
            La imagen no incluye información confidencial: solo tu puntuación global,
            nivel y puntuación por área.
          </p>

          {/* Panel post-share: aparece tras pulsar "Compartir en LinkedIn".
              El compositor de LinkedIn NO acepta texto pre-poblado desde URL,
              así que mostramos el texto listo para pegar (Ctrl+V) y un botón
              alternativo por si el portapapeles se ha perdido en el camino. */}
          {lastShareText && (
            <div
              className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 md:p-5"
              data-testid="share-certificate-post-panel"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                </div>
                <div className="text-[13.5px] text-emerald-900 leading-relaxed">
                  <strong>Listo.</strong> LinkedIn se ha abierto en otra pestaña
                  y tu imagen ya está descargada. En el compositor de LinkedIn:{" "}
                  <strong>pega el texto</strong> (Ctrl+V) y <strong>arrastra la
                  imagen</strong> descargada dentro del post.
                </div>
              </div>
              <textarea
                readOnly
                value={lastShareText}
                onFocus={(e) => e.target.select()}
                className="w-full min-h-[130px] resize-none rounded-lg border border-emerald-200 bg-white p-3 text-[13px] leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="share-certificate-text"
              />
              <div className="mt-3 flex items-center justify-end">
                <button
                  onClick={async () => {
                    await copyText(lastShareText);
                    setCopiedAgain(true);
                    toast.success("Texto copiado al portapapeles");
                    setTimeout(() => setCopiedAgain(false), 2500);
                  }}
                  className="btn-secondary text-sm py-2 px-4"
                  data-testid="share-certificate-copy-text-btn"
                >
                  {copiedAgain ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" strokeWidth={2} /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" strokeWidth={2} /> Copiar texto
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function _roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function _formatDateEs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const months = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ];
  return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
}
